import {
  put,
  call,
  select,
  takeLatest,
  debounce,
  all,
  takeEvery,
} from 'redux-saga/effects';

import { replace } from 'connected-react-router';
import _isEmpty from 'lodash/isEmpty';
import _filter from 'lodash/filter';
import _find from 'lodash/find';
import _map from 'lodash/map';
import _includes from 'lodash/includes';
import _parseInt from 'lodash/parseInt';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _isArray from 'lodash/isArray';
import _lowerCase from 'lodash/lowerCase';
import { SESSION_STATE } from '../constants/AssessmentConstants';
import ActionTypes from '../constants/ActionTypes';
import {
  getLanguageStubApi,
  runCodeWithCustomInput,
  runCodeWithTestCases,
  getTechnologiesApi,
} from '../api/coding';
import {
  setSaveInProgress,
  updateTestExpiryAndSessionState,
} from '../actions/test';
import { getProblemSolution, saveOrUpdateProblemSolution } from '../api/test';
import { showErrorToast } from '../actions/toast';
import { getCodeFromStorage, setCodeToStorage } from '../utils/utils';
import { NETWORK_ERROR, SAVE_IN_PROGRESS } from '../constants/Http';
import { captureException } from '../utils/sentry';
import { getApiErrorData } from '../utils/helperFunction';
import defaultStub from '../defaultStub';
import { problemTypes } from '../constants/ProblemTypes';
import { errorLogger } from '../utils/trackingUtil';

function* handleStubData(args) {
  try {
    let currentStub = null;
    const { problem, selectedLanguageSlug, selectedLanguageUri } = args;
    let { initialCode } = args;
    /**
     * Check if there is a stub in the problem for the default language
     * if yes then use that as the initial code
     */
    if (problem.stubs[selectedLanguageSlug]) {
      if (!initialCode) initialCode = problem.stubs[selectedLanguageSlug];
      currentStub = problem.stubs[selectedLanguageSlug];
    }
    /**
     * This block is execured for problems that are not attempted
     * or if the problem might have been attempted but failed
     * to fetch the solution
     */

    if (initialCode === null || currentStub === null) {
      const stubs = yield select(state => state.coding.stubs);
      try {
        if (selectedLanguageSlug && _isEmpty(stubs[selectedLanguageSlug])) {
          let stub;
          if (problem.problem_type === problemTypes.DBA) {
            stub = {
              content: defaultStub[selectedLanguageSlug],
              q: selectedLanguageSlug,
            };
          } else {
            const resp = yield call(getLanguageStubApi, selectedLanguageSlug);
            stub = resp.data;
          }
          if (!initialCode) initialCode = stub.content;
          if (!currentStub) currentStub = stub.content;
          yield put({
            type: `${ActionTypes.GET_LANGUAGE_STUB}_SUCCESS`,
            payload: stub,
            language: selectedLanguageSlug,
          });
        } else if (
          selectedLanguageSlug &&
          !_isEmpty(stubs[selectedLanguageSlug])
        ) {
          if (!initialCode) initialCode = stubs[selectedLanguageSlug].content;
          if (!currentStub) currentStub = stubs[selectedLanguageSlug].content;
        }
      } catch (e) {
        if (!initialCode) initialCode = '\n';
        if (!currentStub) currentStub = '\n';
        const { toast } = yield select(state => state.appStatus);
        const errorInfo = getApiErrorData(e, 'GCS');
        if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
          yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
        }
      }
    }
    yield put({
      type: `${ActionTypes.SET_CODING_PROBLEM_DATA}_SUCCESS`,
      payload: {
        currentStub,
        code: initialCode,
        enableStubLock: false,
      },
      problemSlug: problem.slug,
    });
    /**
     * Set the initial data for the editor into the solution object
     * corresponding to the problem
     * Also acts as the fallback in case if any of the APIs fail
     */
    yield put({
      type: ActionTypes.SET_INIT_CODE_IN_SOLUTION,
      problemSlug: problem.slug,
      languageUri: selectedLanguageUri,
    });
  } catch (e) {
    errorLogger(e);
    yield put(showErrorToast('An error occured'));
  }
}

function* handleStubLockData(args) {
  try {
    let currentStub = null;
    const { problem, selectedLanguageSlug, selectedLanguageUri } = args;
    let { initialCode } = args;
    /**
     * Check if there is a stub in the problem for the default language
     * if yes then use that as the initial code
     */
    if (problem.stubs[selectedLanguageSlug]) {
      currentStub = problem.stubs[selectedLanguageSlug];
    } else {
      currentStub = {
        head: null,
        body: null,
        tail: null,
      };
    }
    const auth = yield select(state => state.auth);
    const test = yield select(state => state.test);
    const { stub, language } = getCodeFromStorage(
      test.slug,
      problem.slug,
      auth.user.id
    );

    /**
     * If language in localstorage is empty, then initialize it
     */
    if (!language && selectedLanguageSlug && selectedLanguageUri)
      setCodeToStorage(test.slug, problem.slug, auth.user.id, {
        language: selectedLanguageUri,
      });

    let stubCode = !_isEmpty(currentStub.head) ? `${currentStub.head}\n` : '';
    stubCode += !_isEmpty(currentStub.body) ? currentStub.body : '\n\n';
    stubCode += !_isEmpty(currentStub.tail) ? `\n${currentStub.tail}` : '';
    /**
     * If stub from localstorage has changed for this problem, set that as the
     * initial code
     */
    if (
      stub &&
      (stub.body !== currentStub.body ||
        stub.head !== currentStub.head ||
        stub.tail !== currentStub.tail)
    ) {
      setCodeToStorage(test.slug, problem.slug, auth.user.id, {
        stub: currentStub,
      });
      initialCode = stubCode;
    }
    if (!initialCode) initialCode = stubCode;

    yield put({
      type: `${ActionTypes.SET_CODING_PROBLEM_DATA}_SUCCESS`,
      payload: {
        currentStub: stubCode,
        selectedStub: currentStub,
        code: initialCode,
        codeLockLength: {
          head: _isEmpty(currentStub.head)
            ? null
            : currentStub.head.split('\n').length,
          tail: _isEmpty(currentStub.tail)
            ? null
            : currentStub.tail.split('\n').length,
        },
        enableStubLock: true,
      },
      problemSlug: problem.slug,
    });
    /**
     * Set the initial data for the editor into the solution object
     * corresponding to the problem
     * Also acts as the fallback in case if any of the APIs fail
     */
    yield put({
      type: ActionTypes.SET_INIT_CODE_IN_SOLUTION,
      problemSlug: problem.slug,
      languageUri: selectedLanguageUri,
    });
  } catch (e) {
    errorLogger(e);
    yield put(showErrorToast('An error occured'));
  }
}

function* setProblemData(args) {
  try {
    const { problem } = args;
    let allowedTechnologies = [];
    if (!problem) return;
    const stubType = _get(problem, 'extra_data.code_stub_type');
    const hasStubLock =
      _includes(['SCR', 'DBA'], problem.problem_type) &&
      !_isEmpty(problem.stubs) &&
      _includes(
        ['Default', 'Auto-Generated', 'User-Modified', 'User-Generated'],
        stubType
      );
    const { technologiesMapByUri, technologies } = yield select(
      state => state.coding.technologies
    );
    const test = yield select(state => state.test);
    const { allowed_technologies } = test;
    const allowedTechnologiesForProblemFromTest = allowed_technologies
      ? allowed_technologies[problem.slug]
      : undefined;
    /**
     *  Set allowed technologies
     */
    let techs;
    if (problem.test_environment) {
      /**
       * Check if all techs have '/api/v1/technology/'
       */
      techs = _map(problem.technologies, tech =>
        _includes(tech, '/api/v1/technology/')
          ? tech
          : `/api/v1/technology/${tech}`
      );
    } else techs = problem.technologies;

    if (
      _isArray(allowedTechnologiesForProblemFromTest) &&
      !_isEmpty(allowedTechnologiesForProblemFromTest)
    )
      allowedTechnologies = _filter(technologies[problem.problem_type], tech =>
        _includes(allowedTechnologiesForProblemFromTest, tech.resource_uri)
      );
    else if (!_isEmpty(problem.technologies))
      allowedTechnologies = _filter(technologies[problem.problem_type], tech =>
        _includes(techs, tech.resource_uri)
      );
    else allowedTechnologies = technologies[problem.problem_type];

    allowedTechnologies = allowedTechnologies.sort((first, second) => {
      if (first.name < second.name) return -1;
      if (first.name > second.name) return 1;
      return 0;
    });

    let initialCode = null;
    let selectedLanguageUri;
    let selectedLanguageSlug;
    const auth = yield select(state => state.auth);
    const { code, language, stdin } = getCodeFromStorage(
      test.slug,
      problem.slug,
      auth.user.id
    );

    yield put({
      type: `${ActionTypes.SET_CODING_PROBLEM_DATA}_SUCCESS`,
      payload: {
        allowedTechnologies,
        customInput: stdin || '',
        code: code || '',
      },
      problemSlug: problem.slug,
    });
    /**
     * By default set the code and language from localstorage if present
     * @Todo need to get compare the saveTime between client and
     * server before setting the code
     */
    if (code && language) {
      const isAvailable = allowedTechnologies.find(
        lang => lang.resource_uri === language
      );
      if (isAvailable) {
        initialCode = code;
        selectedLanguageUri = isAvailable.resource_uri;
        selectedLanguageSlug = isAvailable.slug;
      }
    }
    const defaultLanguage = yield select(state => state.coding.defaultLanguage);
    if (defaultLanguage) {
      const temp = _find(
        allowedTechnologies,
        technology => technology.slug === defaultLanguage
      );
      /**
       * Check if default language is part of the available languages
       * set that as the selected language
       */
      if (temp && !language && !code) {
        selectedLanguageUri = temp.resource_uri;
        selectedLanguageSlug = defaultLanguage;
      }
    }

    const testSolutionSet = yield select(state => state.testSolutionSet);
    const solution = yield select(state => state.coding.solution);
    const sectionsNotGrouped = yield select(
      state => state.sections?.sectionsNotGrouped
    );
    /**
     * Check if this problem is already attempted. If yes then fetch the solution
     */
    if (!_isEmpty(testSolutionSet) && !_isEmpty(sectionsNotGrouped)) {
      const isSolutionPresent = sectionsNotGrouped.some(section => {
        if (_isArray(section.problems)) {
          return section.problems.some(sectionProblem => {
            return (
              !_isEmpty(sectionProblem.solution) &&
              sectionProblem.slug === problem.slug
            );
          });
        }
        return false;
      });
      if (
        isSolutionPresent &&
        (!solution[problem.slug] || !solution[problem.slug].id)
      ) {
        try {
          const resp = yield call(
            getProblemSolution,
            auth.initUserDetails.user_id,
            auth.initUserDetails.usertype,
            test.slug,
            problem.slug
          );
          yield put({
            type: `${ActionTypes.SAVE_CODING_PROBLEM}_SUCCESS`,
            payload: resp.data,
          });
          const serverCode = _get(resp, 'data.code');
          if (initialCode) {
            /**
             * if there is data in localstorage, assign it to solution
             */
            if (selectedLanguageUri)
              _set(resp, 'data.technology', selectedLanguageUri);
          }
          if (!initialCode && serverCode) {
            initialCode = serverCode;
            selectedLanguageUri = _get(resp, 'data.technology');
            selectedLanguageSlug =
              technologiesMapByUri[selectedLanguageUri].slug;
          }
          if (!selectedLanguageUri)
            selectedLanguageUri = _get(resp, 'data.technology');
          if (!selectedLanguageSlug)
            selectedLanguageSlug =
              technologiesMapByUri[selectedLanguageUri].slug;
        } catch (e) {
          yield put(showErrorToast('Failed to fetch solution'));
        }
      } else if (
        isSolutionPresent &&
        solution[problem.slug] &&
        solution[problem.slug].id
      ) {
        initialCode =
          code ||
          (problemData[problem.slug]
            ? problemData[problem.slug].code
            : initialCode);
        selectedLanguageUri = solution[problem.slug].resource_uri;
        selectedLanguageSlug = technologiesMapByUri[selectedLanguageUri].slug;
      }
    }

    if (!selectedLanguageUri || !selectedLanguageSlug) {
      const [first] = allowedTechnologies;
      selectedLanguageUri = first.resource_uri;
      selectedLanguageSlug = first.slug;
    }
    /**
     * Set the default language in case it is empty
     */
    if (!defaultLanguage) {
      yield put({
        type: ActionTypes.SET_DEFAULT_LANGUAGE,
        language: selectedLanguageSlug,
      });
    }
    if (
      hasStubLock &&
      typeof problem.stubs[selectedLanguageSlug] === 'object'
    ) {
      yield call(handleStubLockData, {
        problem,
        selectedLanguageSlug,
        selectedLanguageUri,
        initialCode,
      });
    } else {
      yield call(handleStubData, {
        problem,
        selectedLanguageSlug,
        selectedLanguageUri,
        initialCode,
      });
    }
  } catch (e) {
    const { toast, slug } = yield select(state => ({
      toast: state.appStatus.toast,
      slug: state.test.slug,
    }));
    let errorInfo = {};
    if (e?.config?.url?.includes('solution?context=')) {
      errorInfo = getApiErrorData(e, 'GUS');
    } else {
      errorLogger(e);
      yield put(showErrorToast('An error occured'));
    }
    if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
    if (e.response?.data?.action?.redirection) {
      yield put(
        updateTestExpiryAndSessionState({
          is_expired: true,
          session_state: SESSION_STATE.END,
        })
      );
      yield put(replace(`/test/${slug}/feedback`));
    }
  }
}

/**
 * Used to switch the theme of editor
 * 0 - light theme
 * 1 - dask theme
 */
function* switchEditorTheme() {
  try {
    const theme = yield select(state => state.coding.theme);
    const newTheme = theme === 'vs' ? 'vs-dark' : 'vs';
    sessionStorage.setItem('fireboltTheme', theme === 'vs' ? 1 : 0);
    yield put({
      type: `${ActionTypes.SWITCH_EDITOR_THEME}_SUCCESS`,
      theme: newTheme,
    });
  } catch (e) {
    yield put(showErrorToast('Failed to change theme'));
  }
}

function* setupCodingEnvironment() {
  try {
    const theme = sessionStorage.getItem('fireboltTheme');
    let selectedTheme = theme;
    if (theme) {
      selectedTheme = _parseInt(theme) === 0 ? 'vs' : 'vs-dark';
    } else {
      selectedTheme = 'vs';
      sessionStorage.setItem('fireboltTheme', 0);
    }

    yield put({
      type: `${ActionTypes.SWITCH_EDITOR_THEME}_SUCCESS`,
      theme: selectedTheme,
    });

    const storedLanguage = sessionStorage.getItem('defaultLanguage');

    yield put({
      type: ActionTypes.SET_DEFAULT_LANGUAGE,
      language: storedLanguage || '',
    });
  } catch (e) {
    yield put(showErrorToast('Failed to load problem'));
  }
}

function* handleChangeLanguageStubLock(args) {
  try {
    const { languageUri, problem } = args;
    const { technologiesMapByUri } = yield select(
      state => state.coding.technologies
    );
    const languageSlug = technologiesMapByUri[languageUri].slug;
    let currentStub;
    if (problem.stubs[languageSlug]) {
      currentStub = problem.stubs[languageSlug];
    } else {
      /**
       * Is stub is not present handle the error by passing null
       */
      currentStub = {
        head: null,
        body: null,
        tail: null,
      };
    }
    const auth = yield select(state => state.auth);
    const test = yield select(state => state.test);

    let stubCode = !_isEmpty(currentStub.head) ? `${currentStub.head}\n` : '';
    stubCode += !_isEmpty(currentStub.body) ? currentStub.body : '\n\n';
    stubCode += !_isEmpty(currentStub.tail) ? `\n${currentStub.tail}` : '';
    setCodeToStorage(test.slug, problem.slug, auth.user.id, {
      stub: currentStub,
      language: languageUri,
      saveTime: new Date().toISOString(),
      code: stubCode,
    });

    yield put({
      type: `${ActionTypes.CHANGE_LANGUAGE}_SUCCESS`,
      currentStub: stubCode,
      languageUri,
      problemSlug: problem.slug,
    });

    yield put({
      type: `${ActionTypes.SET_CODING_PROBLEM_DATA}_SUCCESS`,
      payload: {
        currentStub: stubCode,
        selectedStub: currentStub,
        code: stubCode,
        codeLockLength: {
          head: _isEmpty(currentStub.head)
            ? null
            : currentStub.head.split('\n').length,
          tail: _isEmpty(currentStub.tail)
            ? null
            : currentStub.tail.split('\n').length,
        },
        enableStubLock: true,
      },
      problemSlug: problem.slug,
    });
  } catch (e) {
    yield put(showErrorToast('Failed to change language'));
  }
}

function* handleChangeLanguage(args) {
  try {
    const { languageUri, problem } = args;
    const { technologiesMapByUri } = yield select(
      state => state.coding.technologies
    );
    const languageSlug = technologiesMapByUri[languageUri].slug;
    const stubs = yield select(state => state.coding.stubs);
    const test = yield select(state => state.test);
    const user = yield select(state => state.auth.user);
    let currentStub = null;

    if (!_isEmpty(problem.stubs) && problem.stubs[languageSlug]) {
      currentStub = problem.stubs[languageSlug];
    }

    if (currentStub === null) {
      try {
        if (languageSlug && _isEmpty(stubs[languageSlug])) {
          let stub;
          if (problem.problem_type === problemTypes.DBA) {
            stub = {
              content: defaultStub[languageSlug],
              q: languageSlug,
            };
          } else {
            const resp = yield call(getLanguageStubApi, languageSlug);
            stub = resp.data;
          }
          if (!currentStub) currentStub = stub.content;

          yield put({
            type: `${ActionTypes.GET_LANGUAGE_STUB}_SUCCESS`,
            payload: stub,
            language: languageSlug,
          });
        } else if (
          languageSlug &&
          !_isEmpty(stubs[languageSlug]) &&
          !currentStub
        ) {
          currentStub = stubs[languageSlug].content;
        }
      } catch (e) {
        if (!currentStub) currentStub = '\n';
        const { toast } = yield select(state => state.appStatus);
        const errorInfo = getApiErrorData(e, 'GCS');
        if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
          yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
        }
      }
    }
    yield put({
      type: `${ActionTypes.CHANGE_LANGUAGE}_SUCCESS`,
      currentStub,
      languageUri,
      problemSlug: problem.slug,
    });

    yield put({
      type: `${ActionTypes.SET_CODING_PROBLEM_DATA}_SUCCESS`,
      payload: {
        currentStub,
        code: currentStub,
        enableStubLock: false,
      },
      problemSlug: problem.slug,
    });
    setCodeToStorage(test.slug, problem.slug, user.id, {
      language: languageUri,
      saveTime: new Date().toISOString(),
      code: currentStub,
    });
  } catch (e) {
    yield put(showErrorToast('Failed to change language'));
  }
}

function* onChangeLanguage(args) {
  try {
    const { languageUri, problem } = args;
    const { technologiesMapByUri } = yield select(
      state => state.coding.technologies
    );
    const languageSlug = technologiesMapByUri[languageUri].slug;
    const stubType = _get(problem, 'extra_data.code_stub_type');
    const hasStubLock =
      _includes(['SCR', 'DBA'], problem.problem_type) &&
      !_isEmpty(problem.stubs) &&
      _includes(
        ['Default', 'Auto-Generated', 'User-Modified', 'User-Generated'],
        stubType
      );
    if (hasStubLock && typeof problem.stubs[languageSlug] === 'object') {
      yield call(handleChangeLanguageStubLock, args);
    } else {
      yield call(handleChangeLanguage, args);
    }

    const defaultLanguage = yield select(state => state.coding.defaultLanguage);
    if (defaultLanguage && defaultLanguage !== languageSlug) {
      yield put({
        type: ActionTypes.SET_DEFAULT_LANGUAGE,
        language: languageSlug,
      });
    }
  } catch (e) {
    yield put(showErrorToast('Failed to change language'));
  }
}

/**
 *
 * @param {object} args
 * @param {string|undefined} args.code
 * @param {string} args.problem
 * prepare solution and save
 */
function* prepareSolutionAndSave(args) {
  try {
    const { code, problem, retryCallback } = args;
    const { saveInProgress } = yield select(state => state.appStatus);
    if (saveInProgress && !saveInProgress[problem.slug]) {
      yield put(setSaveInProgress(problem.slug, true));
      const isOffline = yield select(state => state.appStatus.isOffline);
      const { problemData, solution } = yield select(state => state.coding);
      const currentSolution = solution[problem.slug];
      const test = yield select(state => state.test);
      const currentProblemData = problemData[problem.slug];
      const tempCode = code || currentProblemData.code;
      let extra_data;

      if (isOffline) {
        yield put(
          showErrorToast(
            `Could not save solution due to ${_lowerCase(NETWORK_ERROR)}`
          )
        );
        return;
      }

      // prepare data for stublock
      if (currentProblemData.enableStubLock) {
        const { selectedStub } = currentProblemData;
        let headRange = null;
        let tailRange = null;
        if (!_isEmpty(selectedStub.head)) {
          const headLength = selectedStub.head.split('\n').length;
          headRange = `1-${headLength}`;
        }
        if (!_isEmpty(selectedStub.tail)) {
          const tailLength = selectedStub.tail.split('\n').length;
          const codeLength = tempCode.split('\n').length;
          tailRange = `${codeLength + 1 - tailLength}-${codeLength}`;
        }
        extra_data = {
          code: {
            stub_length: currentProblemData.codeLockLength,
            lock_range: {
              head: headRange,
              tail: tailRange,
            },
          },
        };
      }

      const auth = yield select(state => state.auth.initUserDetails);
      const testSolutionSet = yield select(state => state.testSolutionSet);
      const newSolution = {
        creator: `/api/v1/user/${auth.username}`,
        test_solution_set: `api/v1/testsolutionset/${testSolutionSet.id}`,
        test: `/api/v1/test/${test.slug}`,
        technology: solution[problem.slug].technology,
        problem: `/api/v1/problem/${problem.slug}`,
        solution_type: problem.problem_type,
        code: tempCode,
        extra_data,
      };
      const resp = yield call(
        saveOrUpdateProblemSolution,
        auth.user_id,
        auth.usertype,
        test.slug,
        problem.slug,
        newSolution,
        currentSolution.slug,
        undefined,
        retryCallback
      );
      yield put({
        type: ActionTypes.SET_ATTEMPTS_COUNT,
        problem: problem.slug,
      });
      yield put({
        type: `${ActionTypes.SAVE_CODING_PROBLEM}_SUCCESS`,
        payload: resp.data,
      });
      // eslint-disable-next-line consistent-return
      return resp;
    }
    const error = new Error(SAVE_IN_PROGRESS);
    captureException(error);
    throw error;
  } catch (e) {
    const { toast, isTestSubmitted } = yield select(state => ({
      toast: state.appStatus.toast,
      isTestSubmitted: state.test.isTestSubmitted,
    }));
    const errorInfo = getApiErrorData(e, 'UPS');
    if (
      errorInfo &&
      errorInfo.message &&
      !toast?.apiErrorInfo &&
      !args.isAutoSave &&
      !isTestSubmitted
    ) {
      const message = args.code
        ? errorInfo.message?.replace('saving', 'running')
        : errorInfo.message;
      yield put(showErrorToast(message, errorInfo.apiErrorInfo));
    }
    throw e;
  }
}

function* saveOrUpdateCodingSolution(args) {
  const { problem } = args;
  try {
    yield call(prepareSolutionAndSave, args);
  } catch (error) {
    const { toast, isTestSubmitted, slug } = yield select(state => ({
      toast: state.appStatus.toast,
      isTestSubmitted: state.test.isTestSubmitted,
      slug: state.test.slug,
    }));
    const errorInfo = getApiErrorData(error, 'UPS');
    if (
      errorInfo &&
      errorInfo.message &&
      !toast?.apiErrorInfo &&
      !args.isAutoSave &&
      !isTestSubmitted
    ) {
      const message = args.code
        ? errorInfo.message?.replace('saving', 'running')
        : errorInfo.message;
      yield put(showErrorToast(message, errorInfo.apiErrorInfo));
    }
    if (error.response?.data?.action?.redirection) {
      yield put(
        updateTestExpiryAndSessionState({
          is_expired: true,
          session_state: SESSION_STATE.END,
        })
      );
      yield put(replace(`/test/${slug}/feedback`));
    }
    if (error.message !== NETWORK_ERROR && error.message !== SAVE_IN_PROGRESS)
      captureException(error);
  } finally {
    yield put(setSaveInProgress(problem.slug, false));
  }
}

function* setCustomInput(args) {
  try {
    const auth = yield select(state => state.auth.user);
    const test = yield select(state => state.test);
    setCodeToStorage(test.slug, args.problemSlug, auth.id, {
      stdin: args.input ? args.input : '',
    });
    yield put({
      type: `${ActionTypes.SET_CODING_PROBLEM_DATA}_SUCCESS`,
      payload: {
        customInput: args.input ? args.input : '',
      },
      problemSlug: args.problemSlug,
    });
  } catch (e) {
    yield put(showErrorToast('Could not save custom input'));
  }
}

function* onRunCodeWithCustomInput(args) {
  try {
    const auth = yield select(state => state.auth.user);
    const socket = yield select(state => state.socket);
    const { technologiesMapByUri } = yield select(
      state => state.coding.technologies
    );
    const { solution, problemData } = yield select(state => state.coding);
    const currentSolution =
      solution && solution[args.problemSlug] ? solution[args.problemSlug] : {};
    const currentProblemData =
      problemData && problemData[args.problemSlug]
        ? problemData[args.problemSlug]
        : {};

    yield put({
      type: ActionTypes.RESET_RUN_CODE_DATA,
      problemSlug: args.problemSlug,
    });

    const resp = yield call(runCodeWithCustomInput, args.problemType, {
      technology: technologiesMapByUri[currentSolution.technology].slug,
      code: currentProblemData.code,
      input: currentProblemData.customInput,
      problem_id: args.problemId,
      solution_id: currentSolution.id ? currentSolution.id : undefined,
      sockmeta: {
        connid: _get(socket, 'connid'),
        wid: _get(socket, 'wid'),
        sockid: _get(socket, 'sockid'),
      },
      username: auth.username,
    });
    yield put({
      type: `${ActionTypes.RUN_CODE_CUSTOM_INPUT}_SUCCESS`,
      payload: resp.data,
      problemSlug: args.problemSlug,
    });
  } catch (error) {
    yield put({
      type: ActionTypes.ABORT_RUN_CODE,
      problemSlug: args.problemSlug,
    });
    const { toast } = yield select(state => state.appStatus);
    let errorInfo;
    if (isTestQuery) {
      errorInfo = getApiErrorData(error, 'RTQ');
    } else {
      errorInfo = getApiErrorData(error, 'RCD');
    }
    if (error.message !== NETWORK_ERROR && error.message !== SAVE_IN_PROGRESS)
      captureException(error);
    if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
  }
}

function* onRunCodeWithTestCases(args) {
  const { problem, retryCallback } = args;
  try {
    const { solution, problemData } = yield select(state => state.coding);
    const socket = yield select(state => state.socket);
    const currentSolution =
      solution && solution[problem.slug] ? solution[problem.slug] : {};
    const currentProblemData = problemData[problem.slug];

    if (!currentSolution.technology) return null;

    yield put({
      type: ActionTypes.RESET_RUN_CODE_DATA,
      problemSlug: problem.slug,
    });

    const saveSolutionResponse = yield call(prepareSolutionAndSave, {
      code: currentProblemData.code,
      problem,
      retryCallback,
    });
    const solutionId = saveSolutionResponse.data.id;
    if (!solutionId) {
      yield put({
        type: ActionTypes.ABORT_RUN_CODE,
        problemSlug: problem.slug,
      });
      yield put(
        showErrorToast(
          `Something went wrong. It's not you, It's us. Please run code again.`
        )
      );
      return null;
    }
    /**
     * Some times the socket event sends back result for test cases
     * even before the following run code API ends. So set the status
     * as succeded before calling the API
     * */
    yield put({
      type: `${ActionTypes.RUN_CODE_TEST_CASES}_SET_LISTINER`,
      solutionId,
      problem,
    });
    const test = yield select(state => state.test);
    yield call(runCodeWithTestCases, {
      solution_id: solutionId,
      sockmeta: {
        connid: _get(socket, 'connid'),
        wid: _get(socket, 'wid'),
        sockid: _get(socket, 'sockid'),
      },
      test_slug: test.slug,
      solution_slug: currentSolution?.slug || saveSolutionResponse?.data?.slug,
    });

    const auth = yield select(state => state.auth);

    const getSolutionResponse = yield call(
      getProblemSolution,
      auth.initUserDetails.user_id,
      auth.initUserDetails.usertype,
      test.slug,
      problem.slug
    );
    yield put({
      type: `${ActionTypes.SAVE_CODING_PROBLEM}_SUCCESS`,
      payload: getSolutionResponse.data,
    });
  } catch (error) {
    yield put({
      type: ActionTypes.ABORT_RUN_CODE,
      problemSlug: problem.slug,
    });
    const { toast } = yield select(state => state.appStatus);
    let errorInfo = {};
    if (error?.config?.url?.includes('solutions.submit')) {
      errorInfo = getApiErrorData(error, 'SCD');
    } else if (error?.config?.url?.includes('solution?context=')) {
      errorInfo = getApiErrorData(error, 'GUS');
      if (error.response?.data?.action?.redirection) {
        const { slug } = yield select(state => state.test);
        yield put(
          updateTestExpiryAndSessionState({
            is_expired: true,
            session_state: SESSION_STATE.END,
          })
        );
        yield put(replace(`/test/${slug}/feedback`));
      }
    }
    if (error.message !== NETWORK_ERROR && error.message !== SAVE_IN_PROGRESS)
      captureException(error);

    if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
  } finally {
    yield put(setSaveInProgress(problem.slug, false));
  }
  return null;
}

// Saving code to local storage on every code change.
function* saveCodeInLocalStorage(args) {
  const { code, problem } = args;
  try {
    const { problemData, solution } = yield select(state => state.coding);
    const test = yield select(state => state.test);
    const user = yield select(state => state.auth.user);
    if (test.is_expired) return;
    const currentProblemData = problemData[problem.slug];
    const currentSolution = solution[problem.slug];
    const tempCode = code || currentProblemData.code;
    yield call(setCodeToStorage, test.slug, problem.slug, user.id, {
      code: tempCode,
      language:
        currentSolution && currentSolution.technology
          ? currentSolution.technology
          : '',
      saveTime: new Date().toISOString(), // @Todo need to add the offsetTime
    });
    yield put({
      type: `${ActionTypes.SET_CODING_PROBLEM_DATA}_SUCCESS`,
      payload: {
        code: tempCode,
      },
      problemSlug: problem.slug,
    });
  } catch (e) {
    yield put(showErrorToast('Could not store code'));
  }
}

function* getTechnologies() {
  try {
    const resp = yield call(getTechnologiesApi);
    yield put({
      type: `${ActionTypes.GET_TECHNOLOGIES}_SUCCESS`,
      technologies: resp.data.technologies,
    });
  } catch (e) {
    const { toast } = yield select(state => state.appStatus);
    const errorInfo = getApiErrorData(e, 'GTC');
    if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
  }
}

/**
 *
 * @param {object} args
 * @param {string|undefined} args.code
 * @param {string} args.problem
 * args will contains current code and problem
 * In function we will check if firstsave done then call API parallel,
 * otherwise we will save code to local storage
 */
function* parallelSaveCode(args) {
  const { problem } = args;
  const { saveInProgress } = yield select(state => state.appStatus);
  if (saveInProgress && !saveInProgress[problem.slug]) {
    /**
     * all will call every effect parallel
     * ref:https://redux-saga.js.org/docs/advanced/RunningTasksInParallel/#!
     * */
    yield all([
      call(saveCodeInLocalStorage, args),
      call(saveOrUpdateCodingSolution, args),
    ]);
  } else {
    yield call(saveCodeInLocalStorage, args);
  }
}

export default function* loadCodingSaga() {
  yield takeLatest(
    ActionTypes.SETUP_CODING_ENVIRONMENT,
    setupCodingEnvironment
  );
  yield takeLatest(ActionTypes.SET_CODING_PROBLEM_DATA, setProblemData);
  yield takeLatest(ActionTypes.SWITCH_EDITOR_THEME, switchEditorTheme);
  yield takeLatest(ActionTypes.CHANGE_LANGUAGE, onChangeLanguage);
  yield takeLatest(ActionTypes.SAVE_CODING_PROBLEM, saveOrUpdateCodingSolution);
  yield takeLatest(ActionTypes.SET_CODING_CUSTOM_INPUT, setCustomInput);
  yield takeLatest(ActionTypes.RUN_CODE_CUSTOM_INPUT, onRunCodeWithCustomInput);
  yield takeLatest(ActionTypes.RUN_CODE_TEST_CASES, onRunCodeWithTestCases);
  /* 
    Debounce  will delay for 500ms before firing the action.
    ref:https://redux-saga.js.org/docs/api/#debouncems-channel-saga-args
  */
  yield debounce(500, ActionTypes.ON_CODE_CHANGE, saveCodeInLocalStorage);
  /**
   * takeevery call the action parallel
   * ref:https://redux-saga.js.org/docs/api#takeeverypattern-saga-args
   */
  yield takeEvery(ActionTypes.SAVE_CODE_NO_DEBOUNCE, parallelSaveCode);
  yield takeLatest(ActionTypes.GET_TECHNOLOGIES, getTechnologies);
}
